import React, { ReactElement, useEffect, useState } from "react";
import { AiOutlineEdit, AiOutlinePlus, AiOutlineTag } from "react-icons/ai";
import { Form } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import {
    ApprovalFormInput, ApprovalQuestionType,
    FormFieldReplacementInput,
    FormFieldType,
} from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import EditableControl, { ControlType } from "../../editable-text/EditableText";
import { ActionProps } from "./base";
import Select from "../../select/Select";

const SERVICE_ID = "urn:fq:workflow-service:8824f6a945534d91bfc0a99bb3ba07eb";

const TITLE_FIELD = `urn:fq:workflow-service-form-field:(${SERVICE_ID},title)`;
const BODY_FIELD = `urn:fq:workflow-service-form-field:(${SERVICE_ID},body)`;
const USERS_FIELD = `urn:fq:workflow-service-form-field:(${SERVICE_ID},users)`;
const FORM_FIELD = `urn:fq:workflow-service-form-field:(${SERVICE_ID},form)`;

const ID = `urn:fq:workflow-service-action:(${SERVICE_ID},send-approval)`;

const VALUE_FIELD_ID = `urn:fq:workflow-service-action-output:(${ID}, isApproved)`;
const APPROVER_FIELD_ID = `urn:fq:workflow-service-action-output:(${ID}, approver)`;
const APPROVAL_FIELD_ID = `urn:fq:workflow-service-action-output:(${ID}, approvalId)`;

const CHILD_APPROVED = `urn:fq:workflow-service-action-child:(${ID},approved)`;
const CHILD_REJECTED = `urn:fq:workflow-service-action-child:(${ID},not-approved)`;

const DEFAULT_TITLE = "Approval Request";

const TYPE_TO_LABEL = {
    // [ApprovalQuestionType.Text]: "Text",
    [ApprovalQuestionType.Choice]: "Choice",
    [ApprovalQuestionType.MultipleChoice]: "Multiple Choice",
};

const OPTIONS = Object.values(ApprovalQuestionType).map(value => ({
    label: TYPE_TO_LABEL[value],
    value,
}));

function ApprovalFormList({
    form,
    onChange,
}: {
    form: ApprovalFormInput;
    onChange: ((form: ApprovalFormInput) => void);
}): ReactElement {
    const [shouldShowEdit, setShouldShowEdit] = useState(false);

    const [editingId, setEditingId] = useState("");

    const [label, setLabel] = useState("");
    const [description, setDescription] = useState<string | undefined>("");
    const [options, setOptions] = useState("");

    const [type, setType] = useState<ApprovalQuestionType>(ApprovalQuestionType.Choice);

    useEffect(() => {
        if (!shouldShowEdit) {
            setLabel("");
            setDescription("");
            setOptions("");
            setType(ApprovalQuestionType.Choice);
        }
    }, [shouldShowEdit]);

    return (
        <>
            <Confirm
                shouldShow={shouldShowEdit}
                confirmText="Save"
                onHide={() => setShouldShowEdit(false)}
                onConfirm={() => {
                    const questions = [...form.questions];
                    const newValue = {
                        id: label.toLowerCase().replace(/[^a-zA-Z0-9]/, "-"),
                        label,
                        type,
                        description,
                        isRequired: true,
                        options: options.trim().split("\n").map(opt => ({
                            label: opt,
                            value: opt,
                        })),
                    };
                    const foundIndex = editingId ? questions.findIndex(question => question.id === editingId) : -1;
                    if (foundIndex >= 0) {
                        questions[foundIndex] = newValue;
                    } else {
                        questions.push(newValue);
                    }
                    onChange({
                        questions,
                    });
                    setShouldShowEdit(false);
                }}
            >
                <div>
                    <div>
                        <strong className="d-block">Question</strong>
                        <small>The short question being asked</small>
                        <Form.Control
                            type="text"
                            placeholder="What kind of permission..."
                            value={label}
                            onChange={value => setLabel(value.target.value)}
                        />
                    </div>
                    <div className="mt-3">
                        <strong className="d-block">Description</strong>
                        <small>A descriptor to help the user understand how they should be answering this question.</small>
                        <Form.Control
                            type="text"
                            as="textarea"
                            placeholder="Please enter the permission..."
                            value={description}
                            onChange={value => setDescription(value.target.value)}
                        />
                    </div>
                    <div className="mt-3">
                        <strong className="d-block">Type</strong>
                        <small>What is the question type.</small>
                        <Select
                            onChange={value => setType(value.value as ApprovalQuestionType)}
                            value={OPTIONS.find(opt => opt.value === type)}
                            options={OPTIONS}
                        />
                    </div>
                    {(type === ApprovalQuestionType.Choice || type === ApprovalQuestionType.MultipleChoice) && (
                        <div className="mt-3">
                            <strong className="d-block">Choices</strong>
                            <small>What are the choices you want the user to select from? Please enter one per line.</small>
                            <Form.Control
                                type="text"
                                as="textarea"
                                placeholder={"Option 1\nOption 2"}
                                value={options}
                                onChange={value => setOptions(value.target.value)}
                            />
                        </div>
                    )}

                </div>
            </Confirm>
            <div className="list-group">
                {form?.questions.map((question, index) => (
                    <div className="list-group-item d-flex justify-content-between align-items-top" key={question.id}>
                        <div>
                            <div>
                                <strong>{question.label}</strong>
                                <span className="text-light ms-2">

                                    {TYPE_TO_LABEL[question.type]}
                                </span>
                            </div>
                            <small>
                                {question.description}
                            </small>
                        </div>
                        <div>
                            <button
                                type="button"
                                className="btn btn-none p-0 m-0 mx-2"
                                onClick={() => {
                                    setShouldShowEdit(true);
                                    setEditingId(question.id);
                                    setLabel(question.label);
                                    setDescription(question.description as string);
                                    setType(question.type);
                                    setOptions(question.options?.map(opt => opt.label).join("\n") ?? "");
                                }}

                            >
                                <AiOutlineEdit className="me-2 text-light" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-none p-0 m-0 mx-2"
                                onClick={() => {
                                    const copy = [...form.questions];
                                    copy.splice(index, 1);
                                    onChange({
                                        questions: copy,
                                    });
                                }}
                            >
                                <BsTrash className="text-light" />
                            </button>
                        </div>

                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-outline btn-sm my-0 text-start"
                    onClick={() => setShouldShowEdit(true)}
                >
                    <AiOutlinePlus />
                    {" "}
                    Add Question
                </button>

            </div>
        </>
    );
}

export default function ApprovalSettings(props: ActionProps): ReactElement {
    const titleValue = props.values.find(row => row.value.fieldId === TITLE_FIELD)?.value.string as string;
    const bodyValue = props.values.find(row => row.value.fieldId === BODY_FIELD)?.value.string as string;
    const usersValue = props.values.find(row => row.value.fieldId === USERS_FIELD)?.value.options as string[];
    const approvalFormValue = props.values.find(row => row.value.fieldId === FORM_FIELD)?.value.string as string;

    const [title, setTitle] = useState<string | undefined>(titleValue ?? DEFAULT_TITLE);
    const [body, setBody] = useState<string | undefined>(bodyValue);
    const [users, setUsers] = useState<string[] | undefined>(usersValue);

    const [approvalForm, setApprovalForm] = useState<ApprovalFormInput>(
        approvalFormValue ? JSON.parse(approvalFormValue) : { questions: [] },
    );
    const [replacements, setReplacements] = useState<FormFieldReplacementInput[]>(
        () => props.values?.firstElement?.replacement ?? [],
    );

    useEffect(() => {
        if (title || body || users || approvalForm) {
            const questionOutput = approvalForm.questions.map(question => ({
                name: question.label,
                description: question.description ?? question.label,
                id: `urn:fq:workflow-service-action-output:(${ID}, ${question.id})`,
                type: FormFieldType.Text,
            }));
            props.onChange({
                replacements,
                values: [
                    {
                        fieldId: TITLE_FIELD,
                        type: FormFieldType.Text,
                        displayValue: title,
                        string: title,
                    },
                    {
                        fieldId: BODY_FIELD,
                        type: FormFieldType.LongText,
                        displayValue: body,
                        string: body,
                    },
                    {
                        fieldId: USERS_FIELD,
                        type: FormFieldType.Users,
                        options: users,
                    },
                    {
                        fieldId: FORM_FIELD,
                        type: FormFieldType.LongText,
                        string: JSON.stringify(approvalForm),
                    },
                ],
                allowedChildren: [
                    {
                        id: CHILD_APPROVED,
                        label: "Approved",
                        description: "What to do if the action is approved.",
                        output: [
                            {
                                name: "Approver",
                                description: "Who is the user that approved the request",
                                id: APPROVER_FIELD_ID,
                                type: FormFieldType.User,
                            },
                            {
                                name: "Is Approved",
                                description: "Whether the request was approved",
                                id: VALUE_FIELD_ID,
                                type: FormFieldType.Boolean,
                            },
                            {
                                name: "Approval Id",
                                description: "Unique identifier or the approval",
                                id: APPROVAL_FIELD_ID,
                                type: FormFieldType.Text,
                            },
                            ...questionOutput,
                        ],
                    },
                    {
                        id: CHILD_REJECTED,
                        label: "Not Approved",
                        description: "What to do if the action is not approved.",
                        output: [
                            {
                                name: "Rejector",
                                description: "Who is the user that approved the request",
                                id: APPROVER_FIELD_ID,
                                type: FormFieldType.User,
                            },
                            {
                                name: "Is Approved",
                                description: "Whether the request was approved",
                                id: VALUE_FIELD_ID,
                                type: FormFieldType.Boolean,
                            },
                            {
                                name: "Approval Id",
                                description: "Unique identifier or the approval",
                                id: APPROVAL_FIELD_ID,
                                type: FormFieldType.Text,
                            },
                            ...questionOutput,
                        ],
                    },
                ],
            });
        }
    }, [title, body, users, approvalForm.questions]);

    return (
        <div className="">
            <div>
                <strong className="d-block">
                    Title
                </strong>
                <small>
                    The short title to display at the top of the approval.
                </small>
                <div className="">
                    <EditableControl
                        type={ControlType.TEXT}
                        replaceableInput={props.input}
                        isFormField
                        value={title}
                        placeholder="Software Access Request"
                        isEditing
                        replacements={replacements}
                        inputClassName="form-control my-2"
                        onChange={(value, replace) => {
                            setTitle(value as string);
                            if (replace) {
                                setReplacements(curr => curr?.concat(replace)
                                    .dedupe(row => row.fieldId + row.groupId + row.replacement));
                            }
                        }}
                    />

                </div>

            </div>

            <div className="mt-4">
                <strong className="d-block">
                    Description
                </strong>
                <small>
                    The body describing what the user is requesting.
                </small>
                <div className="">
                    <EditableControl
                        type={ControlType.LONG_TEXT}
                        replaceableInput={props.input}
                        isFormField
                        placeholder="{Requester} is requesting accesss to ..."
                        value={body}
                        isEditing
                        replacements={replacements}
                        inputClassName="form-control my-2"
                        onChange={(value, replace) => {
                            setBody(value as string);
                            if (replace) {
                                setReplacements(curr => curr?.concat(replace)
                                    .dedupe(row => row.fieldId + row.groupId + row.replacement));
                            }
                        }}
                    />
                </div>

            </div>

            <div className="mt-4">
                <strong className="d-block">
                    Users
                </strong>
                <small>
                    Which users should be getting the approval request? This could be dynamic fields from
                    other lookups or explict users.
                </small>
                <div className="">
                    <EditableControl
                        type={ControlType.USERS}
                        replaceableInput={props.input}
                        isFormField
                        value={users}
                        isEditing
                        replacements={replacements}
                        placeholder="Users who will receive the approval"
                        inputClassName="form-control my-2"
                        onChange={(value, replace) => {
                            setUsers(value as string[]);
                            if (replace) {
                                setReplacements(curr => curr?.concat(replace)
                                    .dedupe(row => row.fieldId + row.groupId + row.replacement));
                            }
                        }}
                    />
                </div>

            </div>

            <div className="mt-4">
                <strong className="d-block">
                    Questions
                </strong>
                <small>
                    Are there any questions for the approver? The answers to these will be provided with the approval.
                </small>
                <ApprovalFormList form={approvalForm} onChange={setApprovalForm} />
            </div>

        </div>

    );
}
